import { CommonModule, Location } from '@angular/common';
import { Component, Input, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucidePlus,
  lucideX,
  lucideCheck,
  lucideTriangleAlert,
  lucideRefreshCw,
  lucideSearch,
} from '@ng-icons/lucide';
import { UI } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';
import {
  JournalService,
  type AccountSearchResult,
  type JournalEntryCreatePayload,
} from '../../services/journal.service';

/**
 * A single ledger line in the form (debit or credit).
 */
interface FormLine {
  /** Unique client-side ID for tracking. */
  uid: number;
  /** Selected account (null when not yet chosen). */
  account: AccountSearchResult | null;
  /** Amount as string for input binding. */
  amount: string;
  /** Optional per-line description. */
  description: string;
  /** Search query for autocomplete. */
  searchQuery: string;
  /** Autocomplete results. */
  searchResults: AccountSearchResult[];
  /** Whether autocomplete dropdown is open. */
  dropdownOpen: boolean;
  /** Whether we're currently loading suggestions. */
  searching: boolean;
  /** Insufficient balance warning. */
  balanceWarning: string | null;
}

/**
 * Balance validation status per currency.
 */
interface CurrencyBalance {
  currency: string;
  totalDebits: number;
  totalCredits: number;
  difference: number;
  isBalanced: boolean;
}

/**
 * Journal entry creation form with dual debit/credit sections,
 * account autocomplete, real-time balance validation, and dynamic rows.
 */
@Component({
  selector: 'app-journal-create',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, ...UI],
  templateUrl: './journal-create.component.html',
  viewProviders: [
    provideIcons({
      lucidePlus,
      lucideX,
      lucideCheck,
      lucideTriangleAlert,
      lucideRefreshCw,
      lucideSearch,
    }),
  ],
})
export class JournalCreateComponent {
  @Input() entityConfig!: EntityConfig;

  private readonly journalService = inject(JournalService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  private nextUid = 1;

  // ─── Form state ────────────────────────────────────────────

  description = '';
  eventType = 'ADJUSTMENT';

  debitLines = signal<FormLine[]>([this.createEmptyLine()]);
  creditLines = signal<FormLine[]>([this.createEmptyLine()]);

  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);
  readonly submitSuccess = signal(false);

  // ─── Debounce timer per line (keyed by uid) ────────────────

  private readonly debounceTimers = new Map<number, ReturnType<typeof setTimeout>>();

  readonly eventTypes = [
    { value: 'ADJUSTMENT', label: 'Adjustment' },
    { value: 'INITIATION', label: 'Initiation' },
    { value: 'CONFIRMATION', label: 'Confirmation' },
    { value: 'SETTLEMENT', label: 'Settlement' },
  ];

  get entityKey(): string {
    return this.entityConfig?.key ?? 'journal-entries';
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityKey));
  }

  // ─── Line management ──────────────────────────────────────

  private createEmptyLine(): FormLine {
    return {
      uid: this.nextUid++,
      account: null,
      amount: '',
      description: '',
      searchQuery: '',
      searchResults: [],
      dropdownOpen: false,
      searching: false,
      balanceWarning: null,
    };
  }

  addDebitLine(): void {
    this.debitLines.update((lines) => [...lines, this.createEmptyLine()]);
  }

  addCreditLine(): void {
    this.creditLines.update((lines) => [...lines, this.createEmptyLine()]);
  }

  removeDebitLine(uid: number): void {
    this.debitLines.update((lines) => {
      const filtered = lines.filter((l) => l.uid !== uid);
      return filtered.length === 0 ? [this.createEmptyLine()] : filtered;
    });
    this.cleanupDebounceTimer(uid);
  }

  removeCreditLine(uid: number): void {
    this.creditLines.update((lines) => {
      const filtered = lines.filter((l) => l.uid !== uid);
      return filtered.length === 0 ? [this.createEmptyLine()] : filtered;
    });
    this.cleanupDebounceTimer(uid);
  }

  private cleanupDebounceTimer(uid: number): void {
    const timer = this.debounceTimers.get(uid);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(uid);
    }
  }

  // ─── Autocomplete ─────────────────────────────────────────

  onSearchInput(line: FormLine): void {
    line.dropdownOpen = true;
    line.searching = true;

    // Clear existing timer for this line
    this.cleanupDebounceTimer(line.uid);

    // Debounce the search request
    const timer = setTimeout(() => {
      this.fetchAccountSuggestions(line, line.searchQuery);
    }, 200);
    this.debounceTimers.set(line.uid, timer);
  }

  onSearchFocus(line: FormLine): void {
    line.dropdownOpen = true;
    // Fetch suggestions immediately on focus (with current query, even if empty)
    line.searching = true;
    this.fetchAccountSuggestions(line, line.searchQuery);
  }

  private fetchAccountSuggestions(line: FormLine, query: string): void {
    this.journalService.searchAccounts(query, { limit: 15 }).subscribe({
      next: (results) => {
        line.searchResults = results;
        line.searching = false;
      },
      error: () => {
        line.searchResults = [];
        line.searching = false;
      },
    });
  }

  selectAccount(line: FormLine, account: AccountSearchResult): void {
    line.account = account;
    line.searchQuery = `${account.code} — ${account.name}`;
    line.dropdownOpen = false;
    line.searchResults = [];
    line.balanceWarning = null;

    // After selecting, validate balance
    this.validateLineBalance(line, line === this.debitLines().find((l) => l.uid === line.uid) ? 'DEBIT' : 'CREDIT');
  }

  clearAccount(line: FormLine): void {
    line.account = null;
    line.searchQuery = '';
    line.searchResults = [];
    line.dropdownOpen = false;
    line.balanceWarning = null;
  }

  closeDropdown(line: FormLine): void {
    // Delay to allow click to register on dropdown options
    setTimeout(() => {
      line.dropdownOpen = false;
    }, 200);
  }

  // ─── Balance validation per line ──────────────────────────

  onAmountChange(line: FormLine, side: 'DEBIT' | 'CREDIT'): void {
    this.validateLineBalance(line, side);
  }

  private validateLineBalance(line: FormLine, side: 'DEBIT' | 'CREDIT'): void {
    if (!line.account || !line.amount) {
      line.balanceWarning = null;
      return;
    }

    const amount = parseFloat(line.amount);
    if (isNaN(amount) || amount <= 0) {
      line.balanceWarning = null;
      return;
    }

    const balance = parseFloat(line.account.balance);
    const accountType = line.account.account_type;

    // For LIABILITY accounts on debit side: check sufficient balance (balance should be positive for liability)
    if (side === 'DEBIT' && (accountType === 'LIABILITY' || accountType === 'LIABILITY_WALLET')) {
      if (balance < amount) {
        line.balanceWarning = `Insufficient balance: ${line.account.currency_code} ${balance.toFixed(2)} available`;
      } else {
        line.balanceWarning = null;
      }
    }
    // For ASSET accounts on credit side: check sufficient balance
    else if (side === 'CREDIT' && (accountType === 'ASSET_ENTITY_BALANCE')) {
      if (balance < amount) {
        line.balanceWarning = `Insufficient balance: ${line.account.currency_code} ${balance.toFixed(2)} available`;
      } else {
        line.balanceWarning = null;
      }
    } else {
      line.balanceWarning = null;
    }
  }

  // ─── Refresh account balances ──────────────────────────────

  refreshBalances(): void {
    const allLines = [...this.debitLines(), ...this.creditLines()];
    for (const line of allLines) {
      if (line.account) {
        this.journalService.getAccountBalance(line.account.id).subscribe({
          next: (res) => {
            if (line.account) {
              line.account = { ...line.account, balance: res.balance };
              const side = this.debitLines().find((l) => l.uid === line.uid) ? 'DEBIT' : 'CREDIT';
              this.validateLineBalance(line, side);
            }
          },
        });
      }
    }
  }

  // ─── Currency balance totals ───────────────────────────────

  getCurrencyBalances(): CurrencyBalance[] {
    const currencies = new Set<string>();
    const debitTotals = new Map<string, number>();
    const creditTotals = new Map<string, number>();

    for (const line of this.debitLines()) {
      if (line.account && line.amount) {
        const amount = parseFloat(line.amount);
        if (!isNaN(amount) && amount > 0) {
          const cc = line.account.currency_code;
          currencies.add(cc);
          debitTotals.set(cc, (debitTotals.get(cc) ?? 0) + amount);
        }
      }
    }

    for (const line of this.creditLines()) {
      if (line.account && line.amount) {
        const amount = parseFloat(line.amount);
        if (!isNaN(amount) && amount > 0) {
          const cc = line.account.currency_code;
          currencies.add(cc);
          creditTotals.set(cc, (creditTotals.get(cc) ?? 0) + amount);
        }
      }
    }

    return Array.from(currencies)
      .sort()
      .map((currency) => {
        const debits = debitTotals.get(currency) ?? 0;
        const credits = creditTotals.get(currency) ?? 0;
        const diff = Math.abs(debits - credits);
        return {
          currency,
          totalDebits: debits,
          totalCredits: credits,
          difference: diff,
          isBalanced: diff < 0.0001, // Tolerance for floating-point
        };
      });
  }

  isAllBalanced(): boolean {
    const balances = this.getCurrencyBalances();
    return balances.length > 0 && balances.every((b) => b.isBalanced);
  }

  hasAnyLines(): boolean {
    const debitValid = this.debitLines().some((l) => l.account && l.amount && parseFloat(l.amount) > 0);
    const creditValid = this.creditLines().some((l) => l.account && l.amount && parseFloat(l.amount) > 0);
    return debitValid && creditValid;
  }

  canSubmit(): boolean {
    return (
      this.description.trim().length > 0 &&
      this.hasAnyLines() &&
      this.isAllBalanced() &&
      !this.submitting()
    );
  }

  // ─── Section totals ───────────────────────────────────────

  getDebitSectionTotals(): Map<string, number> {
    const totals = new Map<string, number>();
    for (const line of this.debitLines()) {
      if (line.account && line.amount) {
        const amount = parseFloat(line.amount);
        if (!isNaN(amount) && amount > 0) {
          const cc = line.account.currency_code;
          totals.set(cc, (totals.get(cc) ?? 0) + amount);
        }
      }
    }
    return totals;
  }

  getCreditSectionTotals(): Map<string, number> {
    const totals = new Map<string, number>();
    for (const line of this.creditLines()) {
      if (line.account && line.amount) {
        const amount = parseFloat(line.amount);
        if (!isNaN(amount) && amount > 0) {
          const cc = line.account.currency_code;
          totals.set(cc, (totals.get(cc) ?? 0) + amount);
        }
      }
    }
    return totals;
  }

  mapEntries(map: Map<string, number>): [string, number][] {
    return Array.from(map.entries());
  }

  formatNumber(num: number): string {
    return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  // ─── Submit ────────────────────────────────────────────────

  submit(): void {
    if (!this.canSubmit()) return;

    this.submitting.set(true);
    this.submitError.set(null);

    const debitCredits: JournalEntryCreatePayload['debit_credits'] = [];

    for (const line of this.debitLines()) {
      if (line.account && line.amount && parseFloat(line.amount) > 0) {
        debitCredits.push({
          account_id: line.account.id,
          amount: line.amount,
          side: 'DEBIT',
          description: line.description || undefined,
        });
      }
    }

    for (const line of this.creditLines()) {
      if (line.account && line.amount && parseFloat(line.amount) > 0) {
        debitCredits.push({
          account_id: line.account.id,
          amount: line.amount,
          side: 'CREDIT',
          description: line.description || undefined,
        });
      }
    }

    const payload: JournalEntryCreatePayload = {
      description: this.description.trim(),
      event_type: this.eventType,
      debit_credits: debitCredits,
    };

    this.journalService.createJournalEntry(payload).subscribe({
      next: (created) => {
        this.submitting.set(false);
        this.submitSuccess.set(true);
        // Navigate to the new entry detail page
        void this.router.navigate(this.shellRoutes.commands('manage', this.entityKey, String(created.id)));
      },
      error: (err) => {
        this.submitting.set(false);
        const detail =
          err?.error?.detail ??
          err?.error?.debit_credits ??
          err?.error?.non_field_errors ??
          err?.message ??
          'Failed to create journal entry.';
        this.submitError.set(typeof detail === 'string' ? detail : JSON.stringify(detail));
      },
    });
  }

  // ─── Close dropdown on outside click ──────────────────────

  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.autocomplete-wrapper')) {
      for (const line of [...this.debitLines(), ...this.creditLines()]) {
        line.dropdownOpen = false;
      }
    }
  }
}
